import React from 'react'
import {IconButton} from "atmosfair-ui";
import {toast} from "react-toastify";
import ToastWithTextOnly from "../../../molecules/toasts/toast-with-text-only/ToastWithTextOnly";
import {useTranslation} from "react-i18next";
import {ACTIVITY_TYPE, AllActivityTypes, AllActivityTypesArray} from "../../../types-enums/activity";
import {getCo2EForDayOfJourney, getTotalCo2KgEForJourneyViaSumOfDays} from "../../../utils/getter/co2-getter";
import {createArrayFromNumber, DISPLAY_ORDER_TRAVEL_OPTIONS} from "../../../utils/common";
import {getActivitiesByDay, getActivitiesByTravelOptionType} from "../../../utils/getter/activities";
import {Customer} from "../../../api/model/customer";
import {
    isActivityCarBusState,
    isActivityFlightState,
    isActivityHotelState,
    isActivityWithReturnRouteProps
} from "../../../utils/check/type-guards";
import {
    CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL,
    CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL
} from "../../../api/model/common";
import {
    createFlightTripPassengersCountString,
    generateCustomerFlightRouteForClipboardString,
    generateFailedOrSuccessfulFlightLegsString,
    generatFlightCalculationImprovementsString
} from "../../../utils/string/flight";
import {oneActivityCalculatedWithError} from "../../../utils/check/emissions";
import {generateCustomerHotelLocationString} from "../../../utils/string/hotel";
import {DurationProps} from "../../../types-enums/activity-common-properties";
import {
    getTotalCo2KgEEmissionsAndCalculationStatus,
    getTotalCo2KgEEmissionsAndCalculationStatusIncludingReturnActivity,
    TotalCo2KgEEmissionsAndCalculationStatusProps
} from "../../../utils/getter/emissions";
import {getDurationInDaysOrNights} from "../../../utils/getter/common";
import {
    generateCarBusCalculationImprovementsString,
    generateCustomerCarBusInformationString
} from "../../../utils/string/car-bus";
import {
    formatCo2ETotalString,
    generateTotalAndCo2KgEPerPersonString,
    WhereToDisplayActivityString
} from "../../../utils/string/common";
import {useAppSelector} from "../../../redux/reducer/store";
import {BundleType} from "../common";
import {activityHasReturnOption} from "../../../utils/check/activities";
import {isReturnActivityOfAnotherActivity} from "../../../utils/check/common";

export enum WhatToCopy {
    travelComponents = 'travelComponents',
    journey = 'journey'
}

interface CopyToClipboardButtonProps {
    activities: AllActivityTypesArray,
    whatToCopy: BundleType,
    journeyProps?: {
        journeyDuration: DurationProps,
        personCount: number | null
    },
    disabled?: boolean,
}

interface ResultProps {
    successToast: string,
    tooltip: string,
}

const NEW_ACTIVITY_SPACER = '\n\n'

const getProps = (whatToCopy: BundleType) => {
    let result = {} as ResultProps
    switch (whatToCopy) {
        case BundleType.journey:
            result = {
                successToast: "TOASTS.SUCCESS.JOURNEY_COPIED_TO_CLIPBOARD",
                tooltip: "TOOLTIP.COMMON.COPY_JOURNEY_TO_CLIPBOARD"
            }
            break;
        case BundleType.travelComponents:
            result = {
                successToast: "TOASTS.SUCCESS.TRAVEL_COMPONENTS_COPIED_TO_CLIPBOARD",
                tooltip: "TOOLTIP.COMMON.COPY_TRAVEL_COMPONENTS_TO_CLIPBOARD"
            }
            break;
        default:
            whatToCopy satisfies never
    }
    return result
}

const CopyToClipboardButton = ({
                                   activities,
                                   whatToCopy,
                                   disabled,
                               }: CopyToClipboardButtonProps) => {
    const {
        name: travelComponentsName,
        description: travelComponentsDescription,
        customer: travelComponentsCustomer,
        activities: travelComponentActivities
    } = useAppSelector(state => state.travelComponents)
    const {
        name: journeyName,
        description: journeyDescription,
        customer: journeyCustomer,
        journeyDuration: journeyDuration,
        personCount: journeyPersonCount,
        activities: journeyActivities
    } = useAppSelector(state => state.journey)
    const {t, i18n} = useTranslation()
    const {resolvedLanguage} = i18n
    const {tooltip, successToast} = getProps(whatToCopy)

    const _addCommonText = (activitiesText: string[]) => {
        let name: string | number | null = null
        let description: string | null = null
        let customer: Customer | null = null
        switch (whatToCopy) {
            case BundleType.journey:
                name = journeyName
                description = journeyDescription
                customer = journeyCustomer
                break;
            case BundleType.travelComponents:
                name = travelComponentsName
                description = travelComponentsDescription
                customer = travelComponentsCustomer
                break;
            default:
                whatToCopy satisfies never
        }

        if (name !== null) {
            if (typeof name === 'number') {
                activitiesText.push(`${name}\n`)
            } else {
                if (name.length > 0) activitiesText.push(`${name}\n`)
            }
        }

        description !== null && description.length > 0 && activitiesText.push(`${description}\n`)
        if (customer !== null) {
            const {firstName, lastName, email} = customer
            let customerString = `${firstName} ${lastName} ${email}`
            if (customer.company) {
                const {company: {name: companyName}} = customer
                customerString = `${customerString} (${companyName})`
            }
            activitiesText.push(`${customerString}\n`)
        }
    }

    const _generateClipboardDataForActivity = (activity: AllActivityTypes) => {
        const {common: {activityType}} = activity
        let text = ''
        const footnotesForActivityTypes: Set<ACTIVITY_TYPE> = new Set()
        switch (activityType) {
            case ACTIVITY_TYPE.flight:
                if (isActivityFlightState(activity)) {
                    let status: CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL | null = null
                    let emissionKgCO2eTotal: number | null = null
                    const {
                        flightRouteString,
                        showFootNote
                    } = generateCustomerFlightRouteForClipboardString(activity, i18n, false)
                    const {passengerCount, returnId} = activity
                    let commonText = `${text}${flightRouteString}\n${createFlightTripPassengersCountString(
                        Boolean(returnId),
                        0,
                        passengerCount,
                        false,
                        i18n
                    )}`
                    if (status !== CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.failed) {
                        switch (whatToCopy) {
                            case BundleType.travelComponents:
                                let result = {} as TotalCo2KgEEmissionsAndCalculationStatusProps
                                if (isActivityWithReturnRouteProps(activity)) {
                                    result = getTotalCo2KgEEmissionsAndCalculationStatusIncludingReturnActivity(activity, activities)
                                } else {
                                    result = getTotalCo2KgEEmissionsAndCalculationStatus(activity)
                                }
                                status = result.status
                                emissionKgCO2eTotal = result.emissionKgCO2eTotal
                                break;

                            case BundleType.journey:
                                const singleResult = getTotalCo2KgEEmissionsAndCalculationStatus(activity)
                                status = singleResult.status
                                emissionKgCO2eTotal = singleResult.emissionKgCO2eTotal
                                commonText = `${text}${flightRouteString}\n${createFlightTripPassengersCountString(
                                    false,
                                    0,
                                    passengerCount,
                                    isReturnActivityOfAnotherActivity(activity, activities),
                                    i18n,
                                )}`
                                break;

                            default:
                                whatToCopy satisfies never;
                        }
                    }

                    if (status !== CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.failed) {
                        if (showFootNote) footnotesForActivityTypes.add(ACTIVITY_TYPE.flight)
                        if (emissionKgCO2eTotal) {
                            if (activity.common.totalEmissions !== null && oneActivityCalculatedWithError(activity.common.totalEmissions)) {
                                text = `${commonText}\n${generateFailedOrSuccessfulFlightLegsString(activity, CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL.success, i18n)}${NEW_ACTIVITY_SPACER}`
                            } else {
                                text = `${commonText}\n${generateTotalAndCo2KgEPerPersonString(emissionKgCO2eTotal, passengerCount, i18n)}${NEW_ACTIVITY_SPACER}`
                            }
                        }
                    }
                }
                break;
            case ACTIVITY_TYPE.hotel:
                if (isActivityHotelState(activity)) {
                    const {
                        status,
                        emissionKgCO2eTotal
                    } = getTotalCo2KgEEmissionsAndCalculationStatus(activity)
                    if (status !== CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.failed) {
                        const {guestCount} = activity
                        const co2E = emissionKgCO2eTotal
                        const {
                            locationString,
                            returnStayString
                        } = generateCustomerHotelLocationString(activity, i18n, false, false, WhereToDisplayActivityString.travelComponents)
                        const commonText = `${text}${locationString}\n${returnStayString}`
                        if (co2E) text = `${commonText}\n${generateTotalAndCo2KgEPerPersonString(co2E, guestCount, i18n)}${NEW_ACTIVITY_SPACER}`
                    }
                }
                break;
            case ACTIVITY_TYPE.car_bus:
                if (isActivityCarBusState(activity)) {
                    const {waypoints, passengerCount} = activity
                    const {
                        status,
                        emissionKgCO2eTotal
                    } = getTotalCo2KgEEmissionsAndCalculationStatus(activity)
                    if (status !== CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.failed) {
                        const co2E = emissionKgCO2eTotal
                        const {
                            carBusString,
                            showFootnote
                        } = generateCustomerCarBusInformationString(activity, i18n, true)
                        if (showFootnote) footnotesForActivityTypes.add(ACTIVITY_TYPE.car_bus)
                        let commonText = `${text}${carBusString}`
                        if (co2E) text = `${commonText}\n${generateTotalAndCo2KgEPerPersonString(co2E, passengerCount, i18n)}${NEW_ACTIVITY_SPACER}`
                    }
                }
                break;
            default:
                return activityType satisfies never
        }

        return {text, footnotesForActivityTypes}
    }

    /**
     * Function to add footnotes to the clipboard. The footnotes should give hints on how to improve calculation accuracy
     * You can calculate the emissions of this section of the flight more precisely by adding further information such as airline or travel class.
     * You can calculate the emissions more precisely by adding further information such as fuel consumption and distance.
     */
    const _addFootnotes = (activityTypeWithFootnotes: Set<ACTIVITY_TYPE>, text: string) => {
        const footnotes = Array.from(activityTypeWithFootnotes).map((activityType) => {
            switch (activityType) {
                case ACTIVITY_TYPE.flight:
                    return generatFlightCalculationImprovementsString(i18n)
                case ACTIVITY_TYPE.hotel:
                    return null
                case ACTIVITY_TYPE.car_bus:
                    return generateCarBusCalculationImprovementsString(i18n)
                default:
                    activityType satisfies never
            }
        })
            .filter(Boolean)
            .join("\n")
        return `${text}\n${footnotes}`
    }

    const _copyJourneyToClipboard = async () => {
        let activitiesText: string[] = []
        let footnotes: Set<ACTIVITY_TYPE> = new Set()
        const journeyDurationInDays = getDurationInDaysOrNights(journeyDuration, false)
        if (journeyDurationInDays) {
            _addCommonText(activitiesText)
            activitiesText.push(`${t('GENERAL.TRAVELERS_COUNT')}: ${journeyPersonCount}\n`)
            activitiesText.push(`${t('GENERAL.TOTAL_CO2')}:  ${formatCo2ETotalString(getTotalCo2KgEForJourneyViaSumOfDays(activities, journeyDuration, true), resolvedLanguage)} \n\n`)
            createArrayFromNumber(journeyDurationInDays).forEach(day => {
                const activitiesAtDay = getActivitiesByDay(activities, day)
                let activitiesTextPerDay: string[] = []
                activitiesTextPerDay.push(`${t('GENERAL.DAY')} ${day} (${formatCo2ETotalString(getCo2EForDayOfJourney(activities, day, true), resolvedLanguage)})`)
                DISPLAY_ORDER_TRAVEL_OPTIONS.forEach(travelOptionType => {
                    const activitiesPerTravelOption = getActivitiesByTravelOptionType(activitiesAtDay, travelOptionType)
                    const activitiesTextPerTravelOptionType: string[] = []
                    const translatedTravelOption = t(`GENERAL.${travelOptionType.toUpperCase()}`)
                    if (activitiesPerTravelOption.length > 0) {
                        activitiesTextPerTravelOptionType.push(translatedTravelOption)
                        activitiesPerTravelOption.forEach(activity => {
                            const {text, footnotesForActivityTypes} = _generateClipboardDataForActivity(activity)
                            footnotesForActivityTypes.forEach(footnotesForActivityType => footnotes.add(footnotesForActivityType))
                            activitiesTextPerTravelOptionType.push(text)
                        })
                    } else {
                        activitiesTextPerTravelOptionType.push(`${translatedTravelOption}\n${t('GENERAL.NONE')}\n`)
                    }
                    activitiesTextPerDay.push(...activitiesTextPerTravelOptionType)
                })
                activitiesText.push(...activitiesTextPerDay)
                activitiesText.push('\n')
            })
        }

        await navigator.clipboard.writeText(
            _addFootnotes(
                footnotes,
                activitiesText.join("\n")
            )
        )
        _showToast()
    }

    const _showToast = () => toast(<ToastWithTextOnly message={successToast}/>,
        {progress: undefined, type: 'success',});

    const _copyTravelComponentsToClipboard = async () => {
        let activitiesText: string[] = []
        let footnotes: Set<ACTIVITY_TYPE> = new Set()
        let activitiesWithReturn: Set<string> = new Set()
        activities.forEach(activity => {
            if (activityHasReturnOption(activity) && activity.returnId) activitiesWithReturn.add(activity.returnId)
        })
        _addCommonText(activitiesText)
        activities.forEach(activity => {
            if (!activitiesWithReturn.has(activity.common.id)) {
                const {text, footnotesForActivityTypes} = _generateClipboardDataForActivity(activity)
                footnotesForActivityTypes.forEach(activityType => footnotes.add(activityType))
                activitiesText.push(text)
            }
        })

        await navigator.clipboard.writeText(
            _addFootnotes(
                footnotes,
                activitiesText.join("\n")
            )
        )
        _showToast()
    }

    const _copyToClipboard = () => {
        switch (whatToCopy) {
            case BundleType.travelComponents:
                _copyTravelComponentsToClipboard()
                break;
            case BundleType.journey:
                _copyJourneyToClipboard()
                break
            default:
                whatToCopy satisfies never
        }
    }

    const _setDisableButton = () => {
        if (disabled) return true
        switch (whatToCopy) {
            case BundleType.journey:
                return journeyActivities.length === 0
            case BundleType.travelComponents:
                return travelComponentActivities.length === 0
            default:
                whatToCopy satisfies never
        }
    }

    return <IconButton
        disabled={_setDisableButton()}
        tooltip={tooltip}
        color={"primary"}
        size={"large"}
        icon={"copy"}
        onlyIcon={true}
        onClick={_copyToClipboard}/>
}

export default CopyToClipboardButton